import { SLA } from "../CustomizedDashboard/Widgets/SLA/SLAUtils";
import {
  dateFormatType,
  getDaysFromNowDate,
  getFormattedDate,
  isUndefinedOrNull,
} from "@SeemplicityDev/shared";
import type { GroupByState } from "./FindingsData/FindingsNavigationGroup/useFindingsByGroup";
import type { DsComment } from "./FindingDetailsPane/types";
import { shareQueryParams } from "../AppView/shareQueryParams";

// SLAUtils is still untyped (.js). Narrow the consumed shape at the import
// boundary: SLA is a record keyed by sla-status string whose entries carry a
// `title`. TODO: remove once SLAUtils migrates to TS.
const slaByStatus = SLA as Record<string, { title?: string } | undefined>;

// Minimal shape of a finding consumed by parseAdditionalData. Only the fields
// read here are declared; the index signature covers the dynamic field reads
// (e.g. finding[field]). TODO: replace with the canonical Finding domain model.
export interface ParseAdditionalDataFinding {
  additional_data?: string | null;
  last_collected_time?: string | null;
  [key: string]: unknown;
}

export interface ParsedAdditionalData {
  repoLink: string;
  dsComments: DsComment[];
  rawAddData: Record<string, unknown>;
  validISOFormatTimestamp: string | undefined;
  mitigationLevel: string | null;
}

// could not iport general fields directly (probably circular dependancy) - so passing it as prop.
export const parseAdditionalData = (
  finding: ParseAdditionalDataFinding | null | undefined,
  generalFields: string[]
): ParsedAdditionalData => {
  const additionalData = JSON.parse(finding?.additional_data || "{}") || {};
  const repoLink: string = additionalData?.repository_link || "";
  // additional_data is untyped JSON; hacker1_comments carries the DsComment
  // shape consumed downstream (falls back to [] when absent). Cast, not coerce.
  const dsComments = (additionalData?.hacker1_comments || []) as DsComment[];
  const mitigationLevel: string | null =
    additionalData?.mitigation_level || null;

  const getAdditionalData = (): Record<string, unknown> => {
    const additionalDataFiltered = cleanAdditionalData(additionalData, [
      ...generalFields,
      "repository_link", // filter out the repository_link and vulnerability_status (wak-5725) field.
      "vulnerability_status",
      "hacker1_comments", // filter out the hacker1_comments field (wak-20046)
      "controls_datasource_definition_name", // displayed via EDR icon, not as raw additional data
    ]);

    const fieldToAddToAdditionalData = [
      "original_status",
      "finding_type_str",
      "package_name",
      "fix_type",
      "fix_subtype",
      "rule_type",
      "rule_family",
      "rule_id",
    ];

    return {
      ...additionalDataFiltered,
      ...fieldToAddToAdditionalData.reduce<Record<string, unknown>>(
        (acc, field) => {
          if (!!finding?.[field]) {
            acc[field] = finding?.[field];
          }
          return acc;
        },
        {}
      ),
    };
  };

  const rawAddData = getAdditionalData();

  const validISOFormatTimestamp = finding?.last_collected_time?.replace(
    " ",
    "T"
  );

  return {
    repoLink,
    dsComments,
    rawAddData,
    validISOFormatTimestamp,
    mitigationLevel,
  };
};

export const findingEntityEnum = {
  REMEDIATION_ITEM: "REMEDIATION_ITEM",
  SEEMPLICITY_FINDING: "SEEMPLICITY_FINDING",
  DATA_VALIDATION_FINDING: "DATA_VALIDATION_FINDING",
} as const;

export const cloudProviderColors = {
  aws: "var(--cloud-aws-color)",
  sap: "var(--cloud-sap-color)",
  gcp: "var(--cloud-gcp-color)",
  azure: "var(--cloud-azure-color)",
  ecr: "var(--cloud-ecr-color)",
  acr: "var(--cloud-acr-color)",
  docker_hub: "var(--cloud-docker-hub-color)",
  github: "var(--cloud-github-color)",
  gitlab: "var(--cloud-gitlab-color)",
  datacenter: "var(--cloud-datacenter-color)",
  bitbucket: "var(--cloud-bitbucket-color)",
  bitbucket_cloud: "var(--cloud-bitbucket-cloud-color)",
};

export const ManualFinding = "Manual";

export const NAVIGATION_ITEMS_PAGE_SIZE = 5;
export const NAVIGATION_ITEM_WIDTH = 158;

export const getCarouselWidth = (itemsCount: number): number => {
  if (itemsCount) {
    // Preserved verbatim: callers pass a number, but the original accesses
    // `.length` on it (always undefined at runtime), so the comparison is
    // always false and the page-size branch is taken. Behavior unchanged.
    let items =
      (itemsCount as unknown as { length: number }).length <=
      NAVIGATION_ITEMS_PAGE_SIZE
        ? itemsCount
        : NAVIGATION_ITEMS_PAGE_SIZE;
    return items * NAVIGATION_ITEM_WIDTH;
  }

  return 0;
};

export const sortByOptions = [
  {
    label: "Score and Age",
    value: ["SCORE_DESC", "AGE_DESC"],
  },
  {
    label: "Age and Score",
    value: ["AGE_DESC", "SCORE_DESC"],
  },
  {
    label: "SLA",
    value: ["SLA_STATUS_DESC", "SCORE_DESC", "AGE_DESC"],
  },
];

export const groupByTypes = {
  SEVERITY: "finding.severity",
  CLOUD_PROVIDER: "resource.cloud_provider",
  DATASOURCE: "datasource_definition.name",
  CATEGORY: "finding.category",
  SLA_STATUS: "finding.sla_status",
};

export const groupByOptions = [
  {
    label: "Severity",
    value: {
      groupBy: groupByTypes.SEVERITY,
      dbGroupBy: groupByTypes.SEVERITY,
      noData: "Missing Severity",
    },
  },
  {
    label: "Cloud",
    value: {
      groupBy: groupByTypes.CLOUD_PROVIDER,
      dbGroupBy: groupByTypes.CLOUD_PROVIDER,
      noData: "Missing Cloud Provider",
    },
  },
  {
    label: "Data Sources",
    value: {
      groupBy: groupByTypes.DATASOURCE,
      dbGroupBy: groupByTypes.DATASOURCE,
      noData: "Missing Datasource",
    },
  },
  {
    label: "Category",
    value: {
      groupBy: groupByTypes.CATEGORY,
      dbGroupBy: groupByTypes.CATEGORY,
      noData: "Missing Category",
    },
  },
  {
    label: "SLA",
    value: {
      groupBy: groupByTypes.SLA_STATUS,
      dbGroupBy: groupByTypes.SLA_STATUS,
      noData: "Missing SLA",
    },
  },
];

export const findingsQueryParams = shareQueryParams;

export const findingStatuses = {
  11: "Open",
  12: "Open",
  13: "Open",
  14: "Open",
  15: "Reviewed",
  16: "App Owner Testing",
  17: "Migration Planned",
  21: "Fixed",
  22: "Fixed",
  23: "Fixed",
  24: "Fixed",
  25: "Fixed",
  26: "Fixed",
  31: "Ignored",
  32: "Ignored",
  33: "Ignored",
  35: "Ignored",
  36: "Ignored",
};

export const slaStatuses = {
  WITHIN_SLA: {
    title: "On-time",
    icon: "seem-icon-in-time on-time",
  },
  BROKEN_SLA: {
    title: "Overdue",
    icon: "seem-icon-in-time overdue",
  },
  ALMOST_BROKEN_SLA: {
    title: "Due Soon",
    icon: "seem-icon-in-time due-soon",
  },
  NA: {
    title: "No Due Date",
    icon: "",
  },
};

export const getSlaDueDate = (remainingTime?: number | null): string => {
  if (isUndefinedOrNull(remainingTime)) {
    return "";
  }

  const dueDate = getDaysFromNowDate(remainingTime as number).toISODate();

  return getFormattedDate(dueDate ?? undefined, dateFormatType.SHORT);
};

export const aggregatedStatus = {
  OPEN: ["11", "12", "13", "14"],
  OPEN_CONFIRMED: ["15"],
  APP_OWNER_TESTING: ["16"],
  MIGRATION_PLANNED: ["17"],
  FIXED: ["21", "22", "23", "24", "25", "26"],
  IGNORED: ["35", "36"],
  CLOSED: ["21", "22", "23", "24", "25", "26", "31", "32", "33", "35", "36"],
  FALSE_POSITIVE: ["31", "32"],
  EXCEPTION: ["33"],
};

export const findingLastStatusValues = [
  { label: "Open", value: aggregatedStatus.OPEN },
  { label: "Reviewed", value: aggregatedStatus.OPEN_CONFIRMED },
  { label: "Fixed", value: aggregatedStatus.FIXED },
  { label: "Ignored", value: aggregatedStatus.IGNORED },
  { label: "Ignored - False Positive", value: aggregatedStatus.FALSE_POSITIVE },
  { label: "Ignored - Exception", value: aggregatedStatus.EXCEPTION },
];

export const findingSeverities = {
  "-2": "No Priority",
  "-1": "Informational",
  0: "Low",
  1: "Medium",
  2: "High",
  3: "Critical",
};

export const filterTypes = {
  FILTER: "FILTER",
  SCOPE: "SCOPE",
  NONE: "NONE",
  REMEDIATION_ITEM_FILTER: "REMEDIATION_ITEM_FILTER",
  SEEMPLICITY_FINDING_FILTER: "SEEMPLICITY_FINDING_FILTER",
} as const;

export type FilterType = (typeof filterTypes)[keyof typeof filterTypes];

export type SavedFilterType =
  | typeof filterTypes.FILTER
  | typeof filterTypes.REMEDIATION_ITEM_FILTER
  | typeof filterTypes.SEEMPLICITY_FINDING_FILTER;

export const findingActions = {
  SNOOZE: "snooze",
  RESOLVED: "resolved",
  IGNORE: "ignore",
  COMMENT: "comment",
  JIRA: "jira",
};

export const getFindingSeverity = (
  severity: number | string | null | undefined
): string => {
  switch (severity) {
    case -2:
    case "-2":
      return "no_priority";
    case -1:
    case "-1":
      return "informational";
    case 0:
    case "0":
      return "low";

    case 1:
    case "1":
      return "medium";

    case 2:
    case "2":
      return "high";

    case 3:
    case "3":
      return "critical";

    case "all":
      return "all";

    default:
      return "low";
  }
};

interface FilterToExecuteOperand {
  field: string;
  value: string[];
}

interface ParsedFilterToExecute {
  operands: FilterToExecuteOperand[];
}

export const getNameByFilterToExecute = (
  filterToExecute: string
): string | undefined => {
  const jsonFilterToExecute: ParsedFilterToExecute =
    JSON.parse(filterToExecute);
  for (let i = 0; i < jsonFilterToExecute.operands.length; i += 1) {
    if (
      jsonFilterToExecute.operands[i].field === "source" ||
      jsonFilterToExecute.operands[i].field === "ticket_status" ||
      jsonFilterToExecute.operands[i].field === "category" ||
      jsonFilterToExecute.operands[i].field === "tags_values"
    ) {
      return jsonFilterToExecute.operands[i].value[0];
    } else if (jsonFilterToExecute.operands[i].field === "sla_status") {
      return slaByStatus?.[jsonFilterToExecute.operands[i].value[0]]?.title;
    }
  }
};

const isNotEmptyArray = (
  arr: unknown[] // the array has items, and if only one item, it is not an empty string
) => arr?.length > 1 || (arr?.length === 1 && !!arr?.[0]);

const allowedItemType = ["string", "number", "boolean"];
const isValidAdditionalDataValue = (item: unknown): boolean => {
  const expectedContentType =
    (Array.isArray(item) && isNotEmptyArray(item)) ||
    allowedItemType.includes(typeof item);

  return item !== null && item !== "" && expectedContentType;
};

export const cleanAdditionalData = (
  additionalData: Record<string, unknown> | null | undefined,
  fieldsToExclude: string[]
): Record<string, unknown> => {
  return Object.entries(additionalData || {})
    .filter(
      ([k, v]) => !fieldsToExclude.includes(k) && isValidAdditionalDataValue(v)
    )
    ?.reduce<Record<string, unknown>>(
      (acc, [k, v]) => ({ ...acc, [k]: v }),
      {}
    );
};

// Shape of a single group bucket consumed by getGroupName/getGroupTitle.
// Mirrors FindingsGroup in FindingsGroupsResults.tsx; declared locally to avoid
// a circular import (that file imports from here).
interface GroupByEntry {
  value?: string | number | null;
  field?: string;
}

interface GroupByObject {
  group_by: GroupByEntry[];
}

export const getGroupName = (
  groupByObject: GroupByObject
): string | number | null | undefined => {
  let groupByKey:
    | Array<string | number | null | undefined>
    | string
    | undefined;
  if (groupByObject.group_by.length > 1) {
    groupByKey = [];
  }
  groupByObject.group_by.map((el) => {
    if (groupByObject.group_by.length > 1) {
      (groupByKey as Array<string | number | null | undefined>).push(el.value);
    } else {
      groupByKey = el.value?.toString();
    }
  });

  if (Array.isArray(groupByKey)) {
    return JSON.stringify(groupByKey);
  }

  return groupByKey;
};

export const additionalDataSortOrder = [
  "original_score",
  "cvss_score",
  "cvss_v4_score",
  "cvss_v3_score",
  "cvss_v2_score",
  "cvss_v2_temporal_score",
  "tenable_severity_score",
  "cve",
  "cwe",
  "cisa_kev",
  "vulncheck_kev",
  "rce",
  "unauthenticated_rce",
  "epss_percentile",
  "epss_score",
  "scanner",
  "organization",
  "exploit_maturity",
  "result",
  "is_fixable",
  "package",
  "resolution_reason",
  "is_public",
  "public_exposure",
  "operating_system",
  "vulnerable_versions",
  "fixed_versions",
  "contributors",
  "original_severity",
  "engines",
  "branch",
  "project_name",
  "file_name",
  "original_status",
  "original_finding_id",
  "tracking_method",
  "repository_link",
  "url",
  "ip",
  "repository",
  "location",
  "cvss_vector",
  "cvss_v2_vector",
  "cvss_v3_vector",
  "publication_date",
  "vulnerability_reported_date",
  "last_updated_time",
  "agent_last_scan_time",
  "first_found_time",
  "last_found_time",
];

export const getGroupTitle = (
  groupByObject: GroupByObject
): string | number | null | undefined => {
  if (groupByObject.group_by.length > 1) {
    // getGroupName returns a JSON-stringified array in the multi-group case.
    return JSON.parse(getGroupName(groupByObject) as string)[0];
  }

  return getGroupName(groupByObject);
};
export const dataValidationFindingsColumns = {
  ORIGINAL_STATUS: "original_status",
  ORIGINAL_SCORE: "original_score",
  ORIGINAL_ID: "original_finding_id",
  SEEMPLICITY_ID: "seemplicity_int_id",
  ACCOUNT: "cloud_account",
  LAST_COLLECTED_TIME: "last_collected_time",
};

export const findingsColumns = {
  SELECT: "selector",
  ACCOUNT: "cloud_account_friendly_name",
  TITLE: "title",
  SCORE: "score",
  SEVERITY: "severity",
  TICKET_STATUS: "ticket_status",
  STATUS: "actual_status",
  RESOURCES: "resource_name",
  TAGS: "tags",
  TICKET_ID: "ticket_id",
  SLA_STATUS: "sla_status",
  SLA_DUE_DATE: "sla_remaining_time",
  DUE_DATE_STATUS: "ticket_due_date_status",
  AGE: "age",
  TICKET_DUE_DATE: "ticket_due_date",
  ORIGINAL_SEVERITY: "original_severity",
  DISCOVERED_TIME: "discovered_time",
  DATA_SOURCE: "source",
  LAST_REPORTED_TIME: "last_reported_time",
  REMEDIATION_SUB_PHASE: "remediation_sub_phase",
  CATEGORY: "category",
  SUB_CATEGORY: "sub_category",
  AGGREGATED_FINDINGS_COUNT: "aggregated_findings_count",
  FINDING_TYPE: "finding_type_str",
  FIX_ID: "fix_id",
  FIX_VENDOR_ID: "fix_vendor_id",
  FIX_TYPE: "fix_type",
  FIX_SUBTYPE: "fix_subtype",
  RULE_TYPE: "rule_type",
  RULE_FAMILY: "rule_family",
  RULE_ID: "rule_id",
  TICKET_ASSIGNEE_NAME: "ticket_assignee_name",
  EDR_MITIGATION: "mitigation_level",
  REMEDIATION_ITEM_ID: "remediation_item_id",
};

export const sortableFindingsColumns = [
  findingsColumns.SCORE,
  findingsColumns.SLA_STATUS,
  findingsColumns.AGE,
  findingsColumns.DISCOVERED_TIME,
  findingsColumns.CATEGORY,
  findingsColumns.STATUS,
  findingsColumns.LAST_REPORTED_TIME,
  findingsColumns.AGGREGATED_FINDINGS_COUNT,
];

export const sortableSeemplicityFindingsColumns = [
  findingsColumns.SCORE,
  findingsColumns.STATUS,
  findingsColumns.AGE,
  findingsColumns.DISCOVERED_TIME,
  findingsColumns.CATEGORY,
  findingsColumns.LAST_REPORTED_TIME,
];

export const sortableDataValidationFindingsColumns = [
  findingsColumns.DISCOVERED_TIME,
  findingsColumns.LAST_REPORTED_TIME,
  dataValidationFindingsColumns.LAST_COLLECTED_TIME,
  dataValidationFindingsColumns.ORIGINAL_SCORE,
];

export const findingColumnToCsvColumn = {
  actual_status: "status",
};

const slaWeights: Record<string, number> = {
  BROKEN_SLA: 100,
  ALMOST_BROKEN_SLA: 60,
  WITHIN_SLA: 30,
  NA: 0,
};

type GroupSortFn = (a: GroupByObject, b: GroupByObject) => number;

export const sortingFunctionByGroupBy: Record<string, GroupSortFn> = {
  "finding.severity": (a, b) =>
    Number(b.group_by[0].value) - Number(a.group_by[0].value),
  "finding.sla_status": (a, b) =>
    slaWeights[b.group_by?.[0]?.value as string] -
    slaWeights[a.group_by?.[0]?.value as string],
};

export const noSort: GroupSortFn = () => 0;

// Raw summary tuple [groupValue, count] parsed from the findings-by-group query.
type GroupsListDataEntry = [string | number | null, number];

interface BuildGroupsListDataStructureOptions {
  groupsListData?: GroupsListDataEntry[];
  groupBy?: GroupByState;
}

export const buildGroupsListDataStructure = ({
  groupsListData,
  groupBy,
}: BuildGroupsListDataStructureOptions) => {
  return {
    findings_groups: groupsListData
      ?.map(([value, count]) => ({
        total_count: count,
        group_by: [
          {
            value: value,
            // Preserved verbatim: the original assigns the entire groupBy
            // object (not a field string) here. Behavior unchanged.
            field: groupBy as unknown as string,
          },
        ],
      }))
      .sort(
        sortingFunctionByGroupBy[
          groupBy?.selectedOptionValue?.groupBy as string
        ] || noSort
      ),
  };
};

export const findingsVisibleColumns = [
  findingsColumns.TITLE,
  findingsColumns.ACCOUNT,
  findingsColumns.RESOURCES,
  findingsColumns.STATUS,
  findingsColumns.SCORE,
  findingsColumns.TICKET_STATUS,
  findingsColumns.DISCOVERED_TIME,
];

export const seemplicityFindingsVisibleColumns = [
  findingsColumns.TITLE,
  findingsColumns.ACCOUNT,
  findingsColumns.RESOURCES,
  findingsColumns.SCORE,
  findingsColumns.STATUS,
  findingsColumns.DISCOVERED_TIME,
  findingsColumns.REMEDIATION_ITEM_ID,
  findingsColumns.DATA_SOURCE,
];

export const dataValidationFindingsVisibleColumns = [
  findingsColumns.TITLE,
  dataValidationFindingsColumns.ACCOUNT,
  findingsColumns.RESOURCES,
  dataValidationFindingsColumns.ORIGINAL_STATUS,
  dataValidationFindingsColumns.ORIGINAL_SCORE,
  findingsColumns.DISCOVERED_TIME,
  dataValidationFindingsColumns.ORIGINAL_ID,
  dataValidationFindingsColumns.SEEMPLICITY_ID,
  findingsColumns.RULE_TYPE,
  findingsColumns.RULE_ID,
];

export const defaultSortBy = ["SCORE_DESC", "AGE_DESC"];
export const defaultSortByDataValidation = ["LAST_COLLECTED_TIME_DESC"];

export const resourceTypes = {
  SCOPE: "scope",
  GROUP: "group",
  ROOT: "root",
  UNSCOPED: "unscoped",
};

export const unsavedSymbol = "*";

export const getIconClassName = (
  type: string | null | undefined,
  isRecommended?: boolean
): string => {
  if (!type) {
    return "";
  }

  const lowercaseType = type.toLowerCase();

  switch (lowercaseType) {
    case resourceTypes.SCOPE.toLowerCase():
      return "seem-icon-scope";
    case resourceTypes.GROUP.toLowerCase():
      return "seem-icon-scope-group";
    case resourceTypes.UNSCOPED.toLowerCase():
      return "seem-icon-unscoped";
    case "filter":
      return isRecommended
        ? "seem-icon-filter-recommended"
        : "seem-icon-filter";
    case "root":
      return "seem-icon-root";
    default:
      return "";
  }
};

export const entityTypes = {
  FINDING: "finding",
  RESOURCE: "resource",
  DATA_VALIDATION_FINDING: "data validation finding",
};

export const FINDINGS_VALIDATION_FULL_PATH = "discovery/findings-validation";
export const FINDINGS_VALIDATION_RELATIVE_PATH = "findings-validation";

// Column order mirrors the single-finding modal: the finding fields first, then
// the Resource block. It is presentational only — the upload parser matches each
// column by its `mandatoryPart` (`header.split(":")[0]`), never by position, so
// files downloaded from an earlier template still parse.
export const manualFindingCsvFieldsEnum = {
  TITLE: {
    fieldName: "title",
    text: "Title: Enter a short description of the finding",
    mandatoryPart: "Title",
  },
  DESCRIPTION: {
    fieldName: "description",
    text: "Description: (Optional) Enter a detailed description of the finding",
    mandatoryPart: "Description",
  },
  ORIGINAL_SCORE: {
    fieldName: "original_score",
    text: "Original Score: Severity from 0-10 (Critical: 9-10, High: 7-8, Medium: 4-6, Low: 0-3)",
    mandatoryPart: "Original Score",
  },
  DISCOVERED_TIME: {
    fieldName: "discovered_time",
    text: "Discovered Time: Date in YYYY-MM-DD format",
    mandatoryPart: "Discovered Time",
  },
  REMEDIATION_TEXT: {
    fieldName: "remediation text",
    text: "Remediation Text: (Optional) Instructions to fix the issue",
    mandatoryPart: "Remediation Text",
  },
  // `mandatoryPart` is what the upload parser matches on, so it must stay the
  // exact column label, and `fieldName` must stay the exact GraphQL input name.
  CVES: {
    fieldName: "cves",
    text: "CVEs: (Optional) CVE IDs separated by commas. Example: CVE-2021-44228, CVE-2023-34362",
    mandatoryPart: "CVEs",
  },
  CWES: {
    fieldName: "cwes",
    text: "CWEs: (Optional) CWE IDs separated by commas. Example: CWE-79, CWE-89",
    mandatoryPart: "CWEs",
  },
  RESOURCE_NAME: {
    fieldName: "resource name",
    text: "Resource Name: Name of the resource (e.g., User, Machine, Repo)",
    mandatoryPart: "Resource Name",
  },
  RESOURCE_TYPE: {
    fieldName: "resource type",
    text: "Resource Type: Type of resource (e.g., EC2, API Gateway)",
    mandatoryPart: "Resource Type",
  },
  CLOUD_ACCOUNT: {
    fieldName: "account",
    text: "Account: Account information (e.g., Cloud account name, Repository name)",
    mandatoryPart: "Account",
  },
  RESOURCE_TAGS: {
    fieldName: "resource_tags",
    text: "Resource Tags: (Optional) Values separated by commas (tag1 , tag2, tag3)",
    mandatoryPart: "Resource Tags",
  },
  // Grouped with the resource columns to match the modal, but both map to
  // finding-level keys on `ManualFindingInput`, not to entries in `resources[]`.
  FQDN: {
    fieldName: "fqdn",
    text: "FQDN: (Optional) Fully qualified domain name without a protocol, port, or path. Example: api.prod.example.com",
    mandatoryPart: "FQDN",
  },
  CLOUD_RESOURCE_ID: {
    fieldName: "cloud_resource_id",
    text: "Cloud Resource ID: (Optional) AWS ARN, Azure Resource ID, or GCP full resource name. Example: arn:aws:ec2:us-east-1:123456789012:instance/i-0123456789abcdef0",
    mandatoryPart: "Cloud Resource ID",
  },
};

export const csvTemplateForManualFindings = [
  Object.values(manualFindingCsvFieldsEnum).map((field) => field.text),
  ["Start entering values in this line"],
];

export const csvTemplateForManualFindingsXlsx = Object.values(
  manualFindingCsvFieldsEnum
).map((field) => ({
  [field.text]:
    field?.fieldName === "title" ? "Start entering values in this line" : "",
}));

export const findingsCheckType = {
  ALL: "ALL",
  MULTI: "MULTI",
  SINGLE: "SINGLE",
} as const;

export type FindingsCheckType =
  (typeof findingsCheckType)[keyof typeof findingsCheckType];

// Escape HTML-significant characters so JSON content is rendered as literal text
// inside <pre><code> and can never be interpreted as markup (defeats XSS via the
// document.write sink below — js/incomplete-multi-character-sanitization).
const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const openJSONStringInNewWindow = (
  data: unknown,
  openTab = true
): string | undefined => {
  let tabData = ``;

  // Recursive function to parse stringified JSON objects inside any part of the data
  const parseStringifiedJSON = (obj: unknown): unknown => {
    if (typeof obj === "string") {
      try {
        // Try to parse the string as JSON. If it succeeds, return the parsed object.
        return JSON.parse(obj);
      } catch (e) {
        // If parsing fails, just return the original string
        return obj;
      }
    }
    // If it's not a string, recursively process it if it's an object or an array
    if (Array.isArray(obj)) {
      return obj.map(parseStringifiedJSON);
    }
    if (typeof obj === "object" && obj !== null) {
      const record = obj as Record<string, unknown>;
      Object.keys(record).forEach((key) => {
        record[key] = parseStringifiedJSON(record[key]);
      });
    }
    return obj;
  };

  // Recursively process the entire data object to remove unnecessary quotes around JSON objects
  data = parseStringifiedJSON(data);

  // Convert the cleaned data into a properly formatted string
  const dataToString = JSON.stringify(data, undefined, 2);
  tabData = dataToString;

  // Clean the string by removing escape characters, redundant spaces, and extra quotes
  tabData = tabData
    .replace(/\\n/g, "")
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, "\\")
    .replace(/  +/g, " ")
    .trim();

  // Always HTML-escape as the final step before the data enters the document.write
  // template, so injected markup is rendered as inert literal text regardless of input.
  tabData = escapeHtml(tabData);

  // Open the data in a new window
  if (openTab) {
    const tab = window.open("about:blank", "_blank");
    // Preserved verbatim: original does not null-check the opened window.
    tab!.document.write(
      "<html><body><pre><code>" + tabData + "</code></pre></body></html>"
    );
    tab!.document.close();
  } else {
    return tabData;
  }
};
